import { useHistory } from 'react-router';
import { ConfirmationModal, Typography, useOverlayContext } from 'seagull';
import MissingSignaturesRecipients from "../components/MissingSignaturesRecipients/MissingSignaturesRecipients";
import RestfulWrapper from "../components/RestfulWrapper/RestfulWrapper";
import { routes } from "../constants";
import { useEventReporter, useStoreActions } from "../contexts";
import { useDraftProposal } from "./reactQuery/mutations/useDraftProposal";
import { Intent } from "../infra/access-control";
import { useDuplicateProposal } from "./reactQuery/mutations/useDuplicateProposal";
import { useCopyProposalLink } from "./useCopyProposalLink";
import { useSendReminder } from "./useSendReminder";
import { useTerminateAgreement } from "./useTerminateAgreement";
import { useWithdrawToEditProposal } from "./useWithdrawToEditProposal";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const MissingSignaturesReminderModal = _ref => {
  let {
    relationship,
    dismissModal
  } = _ref;
  const {
    sendReminder
  } = useSendReminder();
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    textAlignCenter: false,
    title: "Send reminder",
    primaryButtonLabel: "Send now",
    primaryButtonTestId: "send-propposal-approval-reminder-button",
    secondaryButtonLabel: "Back",
    secondaryButtonTestId: "cancel-send-propposal-approval-reminder-button",
    onCloseButtonClick: dismissModal,
    onPrimaryButtonClick: () => {
      sendReminder(relationship);
      dismissModal();
    },
    onSecondaryButtonClick: dismissModal,
    content: /*#__PURE__*/_jsxs("div", {
      "data-testid": "send-proposal-approval-reminder-modal",
      children: [/*#__PURE__*/_jsx(Typography, {
        children: "An email reminder will be sent to recipients who need to sign:"
      }), /*#__PURE__*/_jsx(MissingSignaturesRecipients, {
        relationship: relationship
      })]
    })
  });
};
export function useProposalActions() {
  const {
    showModal
  } = useOverlayContext();
  const {
    api
  } = useStoreActions();
  const {
    withdrawToEditProposalByIdWithConfirmation
  } = useWithdrawToEditProposal();
  const {
    reportToGtm,
    reportToAppcues
  } = useEventReporter();
  const {
    copyAgreementLink
  } = useCopyProposalLink();
  const {
    cancelProposalById
  } = useTerminateAgreement();
  const history = useHistory();
  const {
    draftProposal
  } = useDraftProposal({
    onSuccess: newDraftId => history.push(routes.draft(newDraftId))
  });
  const {
    duplicateProposal
  } = useDuplicateProposal({
    onSuccess: newDraftId => history.push(routes.draft(newDraftId)),
    withPageLoader: true
  });
  return {
    getEditProposalAction(proposal, origin) {
      const fromProposalDashboard = origin === 'proposalsDashboard';
      return {
        text: fromProposalDashboard ? 'Edit' : 'Edit proposal',
        intent: Intent.EditProposal,
        disabled: proposal.isSample,
        onClick: async () => {
          if (fromProposalDashboard) {
            withdrawToEditProposalByIdWithConfirmation(proposal.id, proposal.draftId);
          } else {
            const confirmed = await showModal({
              closeOnClickOutside: true,
              closeOnEscapeKey: true,
              content: _ref2 => {
                let {
                  dismissModal
                } = _ref2;
                return /*#__PURE__*/_jsx(ConfirmationModal, {
                  textAlignCenter: false,
                  skin: "danger",
                  title: "Withdraw to edit",
                  content: "To edit this proposal's details, it is necessary to withdraw it first. As soon as you send the new proposal, an email will be sent with a link to the new version.",
                  primaryButtonLabel: "Withdraw and edit",
                  primaryButtonTestId: "confirm-withdraw-to-edit",
                  secondaryButtonLabel: "Cancel",
                  onPrimaryButtonClick: () => dismissModal(true),
                  onSecondaryButtonClick: () => dismissModal(false)
                });
              }
            });
            if (!confirmed) return;
            draftProposal({
              relationshipId: proposal.id,
              draftId: proposal.draftId
            });
          }
        }
      };
    },
    getSendProposalReminderAction(proposalId, isSampleProposal) {
      return {
        text: 'Send reminder',
        intent: Intent.SendProposalReminder,
        disabled: isSampleProposal,
        onClick: () => {
          showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: _ref3 => {
              let {
                dismissModal
              } = _ref3;
              return /*#__PURE__*/_jsx(RestfulWrapper, {
                fetch: () => api.relationship.get(proposalId),
                renderChild: _ref4 => {
                  let {
                    data
                  } = _ref4;
                  return /*#__PURE__*/_jsx(MissingSignaturesReminderModal, {
                    dismissModal: dismissModal,
                    relationship: data
                  });
                }
              });
            }
          });
        }
      };
    },
    getDuplicateProposalAction(proposalId, _ref5) {
      let {
        isSampleProposal,
        draftId
      } = _ref5;
      return {
        text: 'Duplicate',
        intent: Intent.DuplicateProposal,
        disabled: isSampleProposal,
        onClick: () => {
          duplicateProposal({
            relationshipId: proposalId,
            draftId
          });
        }
      };
    },
    getCopyProposalLinkAction(proposal) {
      return {
        text: 'Copy link',
        intent: Intent.CopyRelationshipDirectLink,
        disabled: false,
        onClick: () => {
          reportToGtm('button-click', 'vendor-copy-link-proposal', {
            progressStatus: proposal.progressStatus
          });
          reportToAppcues('button-click', 'vendor-copy-link-proposal');
          copyAgreementLink({
            agreementId: proposal.id,
            vendorId: proposal.vendor.id,
            customerId: proposal.customer.id,
            progressStatus: proposal.progressStatus,
            withGlobalLoader: true
          });
        }
      };
    },
    getCancelProposalAction(proposalId, onSuccess) {
      return {
        text: 'Withdraw',
        disabled: false,
        intent: Intent.CancelProposal,
        onClick: () => cancelProposalById(proposalId, onSuccess)
      };
    }
  };
}